import axios from 'axios';
import {
  User,
  TinnitusProfile,
  TinnitusProfileCreate,
  QuestionnaireResult,
  QuestionnaireResultCreate,
  TreatmentBookmark,
  TreatmentBookmarkCreate,
  HearingTestResult,
  HearingTestResultCreate,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach Cognito ID token to every request if present
api.interceptors.request.use((config) => {
  // Avoid circular import by reading from the Cognito SDK directly
  const { CognitoUserPool } = require('amazon-cognito-identity-js');
  const pool = new CognitoUserPool({
    UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID!,
    ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID!,
  });
  const currentUser = pool.getCurrentUser();
  if (!currentUser) return config;

  // getSession is async; use a synchronous token retrieval via localStorage key
  // The Cognito SDK stores the token under a predictable key.
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID!;
  const username = currentUser.getUsername();
  const tokenKey = `CognitoIdentityServiceProvider.${clientId}.${username}.idToken`;
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers = config.headers ?? {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});


// User API
export const userApi = {
  register: async (email: string): Promise<User> => {
    const response = await api.post('/users/register', { email });
    return response.data;
  },
  me: async (): Promise<User> => {
    const response = await api.get('/users/me');
    return response.data;
  },
};

// Tinnitus Profile API
export const tinnitusProfileApi = {
  create: async (profileData: TinnitusProfileCreate): Promise<TinnitusProfile> => {
    const response = await api.post('/tinnitus-profiles/', profileData);
    return response.data;
  },
  getMyProfiles: async (): Promise<TinnitusProfile[]> => {
    const response = await api.get('/tinnitus-profiles/');
    return response.data;
  },
};

// Questionnaire API
export const questionnaireApi = {
  create: async (resultData: QuestionnaireResultCreate): Promise<QuestionnaireResult> => {
    const response = await api.post('/questionnaire-results/', resultData);
    return response.data;
  },
  getMyResults: async (): Promise<QuestionnaireResult[]> => {
    const response = await api.get('/questionnaire-results/');
    return response.data;
  },
};

// Treatment Bookmark API
export const treatmentBookmarkApi = {
  create: async (bookmarkData: TreatmentBookmarkCreate): Promise<TreatmentBookmark> => {
    const response = await api.post('/treatment-bookmarks/', bookmarkData);
    return response.data;
  },
  getMyBookmarks: async (): Promise<TreatmentBookmark[]> => {
    const response = await api.get('/treatment-bookmarks/');
    return response.data;
  },
};

// Hearing Test API
export const hearingTestApi = {
  create: async (resultData: HearingTestResultCreate): Promise<HearingTestResult> => {
    const response = await api.post('/hearing-test-results/', resultData);
    return response.data;
  },
  getMyResults: async (): Promise<HearingTestResult[]> => {
    const response = await api.get('/hearing-test-results/');
    return response.data;
  },
};

// Progress API
export interface ProgressData {
  questionnaire_results: QuestionnaireResult[];
  tinnitus_profiles: TinnitusProfile[];
}

export const progressApi = {
  getMyProgress: async (): Promise<ProgressData> => {
    const response = await api.get('/progress/');
    return response.data;
  },
};

// Audio Calibration API
export interface CalibrationRequest { target_dbfs: number }
export interface CalibrationResponse {
  status: string;
  message?: string;
  final_measurement?: number;
  target_dbfs?: number;
  iterations?: number;
  final_volume?: number;
  ambient_level?: number;
}
export interface CalibrationStatus {
  is_calibrating: boolean;
  current_measurement: number;
  target_dbfs: number;
  current_volume: number;
  status_message: string;
  ambient_noise_level?: number;
  binary_search_mode: boolean;
}

export const calibrationApi = {
  start: async (request: CalibrationRequest): Promise<CalibrationResponse> => {
    const response = await api.post('/calibration/start', request);
    return response.data;
  },
  stop: async (): Promise<{ message: string }> => {
    const response = await api.post('/calibration/stop');
    return response.data;
  },
  getStatus: async (): Promise<CalibrationStatus> => {
    const response = await api.get('/calibration/status');
    return response.data;
  },
};

export default api;
